import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Plus, Edit2, Trash2, Package, AlertTriangle, Check, X, ChevronLeft, ChevronRight, Search, DollarSign, Boxes } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingInventory, setEditingInventory] = useState(null);
  const [inventoryValue, setInventoryValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    );
  }, [products, search]);

  // Inventory summary across the entire catalog (not affected by search)
  const summary = useMemo(() => {
    let totalUnits = 0;
    let totalValue = 0;
    let lowStock = 0;
    let outOfStock = 0;
    products.forEach(p => {
      const qty = p.inventory_quantity || 0;
      const unitPrice = p.sale_price || p.price || 0;
      totalUnits += qty;
      totalValue += qty * unitPrice;
      if (qty === 0) outOfStock += 1;
      else if (qty <= 10) lowStock += 1;
    });
    return { totalUnits, totalValue, lowStock, outOfStock };
  }, [products]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/products/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/products/admin/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
      console.error('Error deleting product:', error);
    }
  };

  const handleInventoryEdit = (product) => {
    setEditingInventory(product.id);
    setInventoryValue(product.inventory_quantity?.toString() || '0');
  };

  const handleInventorySave = async (productId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const quantity = parseInt(inventoryValue);
      
      if (isNaN(quantity) || quantity < 0) {
        toast.error('Please enter a valid quantity');
        return;
      }

      await axios.patch(
        `${API_URL}/api/admin/inventory/product/${productId}`,
        { inventory_quantity: quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Inventory updated successfully');
      setEditingInventory(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update inventory');
      console.error(error);
    }
  };

  const handleInventoryCancel = () => {
    setEditingInventory(null);
    setInventoryValue('');
  };

  const getInventoryBadge = (quantity) => {
    if (quantity === undefined || quantity === null) quantity = 0;
    
    if (quantity === 0) {
      return <span className="text-red-600 font-semibold flex items-center gap-1">
        <AlertTriangle className="w-4 h-4" />
        Out of Stock
      </span>;
    }
    if (quantity <= 5) {
      return (
        <span className="text-red-500 font-semibold flex items-center gap-1">
          <AlertTriangle className="w-4 h-4" />
          {quantity} (Critical!)
        </span>
      );
    }
    if (quantity <= 10) {
      return <span className="text-yellow-600 font-semibold">{quantity} (Low Stock)</span>;
    }
    return <span className="text-green-600 font-semibold">{quantity} in stock</span>;
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500 mt-1">Manage your product catalog and inventory</p>
        </div>
        <Button
          onClick={() => navigate('/admin/products/new')}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="admin-add-product-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Inventory Summary Cards */}
      {!loading && products.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-blue-500" data-testid="summary-products">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Products</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{products.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500/30" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-green-500" data-testid="summary-units">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Units in Stock</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalUnits.toLocaleString()}</p>
              </div>
              <Boxes className="w-8 h-8 text-green-500/30" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-emerald-500" data-testid="summary-value">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inventory Value</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ${summary.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500/30" />
            </div>
          </div>

          <div
            className={`bg-white rounded-lg shadow p-5 border-l-4 ${summary.outOfStock > 0 ? 'border-red-500' : summary.lowStock > 0 ? 'border-yellow-500' : 'border-gray-300'}`}
            data-testid="summary-alerts"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Stock Alerts</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {summary.outOfStock + summary.lowStock}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {summary.outOfStock} out · {summary.lowStock} low
                </p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${summary.outOfStock > 0 ? 'text-red-500/40' : summary.lowStock > 0 ? 'text-yellow-500/40' : 'text-gray-400/30'}`} />
            </div>
          </div>
        </div>
      )}

      {/* Search bar */}
      {!loading && products.length > 0 && (
        <div className="mb-4 relative max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, SKU, or category..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            data-testid="admin-products-search"
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No products found</p>
          <Button onClick={() => navigate('/admin/products/new')}>
            Add Your First Product
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">PRODUCT</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">CATEGORY</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">PRICE</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    INVENTORY
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">STATUS</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedProducts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No products match your search.
                  </td>
                </tr>
              ) : paginatedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={product.images[0] || 'https://via.placeholder.com/60'}
                        alt={product.title}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div>
                        <p className="font-semibold text-gray-900">{product.title}</p>
                        <p className="text-sm text-gray-500">{product.sku}</p>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span className="text-gray-700">{product.category}</span>
                  </td>

                  <td className="px-6 py-4">
                    <div>
                      <span className="text-gray-900 font-semibold">
                        ${product.sale_price || product.price}
                      </span>
                      {product.sale_price && (
                        <span className="text-gray-500 text-sm line-through ml-2">
                          ${product.price}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Inventory Column with Inline Editing */}
                  <td className="px-6 py-4">
                    {editingInventory === product.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={inventoryValue}
                          onChange={(e) => setInventoryValue(e.target.value)}
                          className="w-20 px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleInventorySave(product.id);
                            if (e.key === 'Escape') handleInventoryCancel();
                          }}
                        />
                        <button
                          onClick={() => handleInventorySave(product.id)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Save"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleInventoryCancel}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Cancel"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => handleInventoryEdit(product)}
                        className="cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors group"
                        title="Click to edit inventory"
                      >
                        {getInventoryBadge(product.inventory_quantity)}
                        <div className="text-xs text-gray-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Click to edit
                        </div>
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {product.stock_status === 'in_stock' && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                          Active
                        </span>
                      )}
                      {product.stock_status === 'out_of_stock' && (
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                          Out of Stock
                        </span>
                      )}
                      {product.is_featured && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          Featured
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Product"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Product"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600" data-testid="admin-products-pagination-info">
              Showing{' '}
              <span className="font-medium">
                {filteredProducts.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
              </span>
              {' '}to{' '}
              <span className="font-medium">
                {Math.min(currentPage * PAGE_SIZE, filteredProducts.length)}
              </span>
              {' '}of{' '}
              <span className="font-medium">{filteredProducts.length}</span>
              {' '}products
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="admin-products-prev-page"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-gray-600 px-2">
                Page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="admin-products-next-page"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
